import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { EMAIL_INPUT_PATTERN, EMAIL_INPUT_TITLE, isValidEmailAddress } from '../utils/emailValidation';
import './Login.css';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('login');
  const [resetEmail, setResetEmail] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [info, setInfo] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!isValidEmailAddress(formData.email)) {
      setError('Veuillez saisir une adresse email valide contenant @.');
      return;
    }
    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);
      if (result.success) {
        // Check if user is admin and redirect accordingly
        navigate(result.isAdmin ? '/admin' : '/dashboard');
      } else if (result.code === 'PASSWORD_RESET_REQUIRED') {
        const forcedEmail = String(result?.meta?.email || formData.email || '').trim();
        const otpSent = result?.meta?.otpSent !== false;

        setResetEmail(forcedEmail);
        setResetOtp('');
        setResetToken('');
        setNewPassword('');
        setConfirmPassword('');

        if (otpSent) {
          setInfo('Premiere connexion detectee. Un code de verification a ete envoye a votre email.');
          setMode('forgot-otp');
        } else {
          setInfo('Premiere connexion detectee. Veuillez demander un code de reinitialisation pour continuer.');
          setMode('forgot-email');
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const startForgotPassword = () => {
    setError('');
    setInfo('');
    setResetEmail(formData.email || '');
    setResetOtp('');
    setResetToken('');
    setNewPassword('');
    setConfirmPassword('');
    setMode('forgot-email');
  };

  const submitForgotEmail = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!isValidEmailAddress(resetEmail)) {
      setError('Veuillez saisir une adresse email valide contenant @.');
      return;
    }
    setLoading(true);
    try {
      await api.auth.requestPasswordReset(String(resetEmail || '').trim());
      setInfo('Code envoyé. Vérifiez votre email.');
      setMode('forgot-otp');
    } catch (err) {
      setError(err.response?.data?.error || "Email isn't registered");
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const response = await api.auth.verifyPasswordResetOtp(String(resetEmail || '').trim(), String(resetOtp || '').trim());
      const token = response?.data?.resetToken;
      if (!token) {
        setError('Invalid code. Please try again.');
        return;
      }
      setResetToken(token);
      setMode('forgot-reset');
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resendOtp = async () => {
    setError('');
    setInfo('');
    if (!isValidEmailAddress(resetEmail)) {
      setError('Veuillez saisir une adresse email valide contenant @.');
      return;
    }
    setLoading(true);
    try {
      await api.auth.requestPasswordReset(String(resetEmail || '').trim());
      setInfo('Code renvoyé. Vérifiez votre email.');
    } catch (err) {
      setError(err.response?.data?.error || "Email isn't registered");
    } finally {
      setLoading(false);
    }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    const pw = String(newPassword || '');
    const pw2 = String(confirmPassword || '');
    if (pw.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (pw !== pw2) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await api.auth.resetPassword(String(resetEmail || '').trim(), resetToken, pw);
      setInfo('Password reset successfully. You can now sign in.');
      setFormData((p) => ({ ...p, email: resetEmail, password: '' }));
      setMode('login');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-left">
          <div className="login-branding">
            <h1>ADEEF</h1>
            <p>Association Des Étudiants Étrangers en France</p>
            <div className="features-list">
              <div className="feature-item">
                <span className="feature-icon">🏠</span>
                <span>Accès à votre espace locataire</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">📋</span>
                <span>Suivi de votre demande</span>
              </div>
              <div className="feature-item">
                <span className="feature-icon">💳</span>
                <span>Gestion des paiements</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="login-right">
          <div className="login-card">
            <div className="login-header">
              <h2>
                {mode === 'login' ? 'Connexion' : mode === 'forgot-email' ? 'Mot de passe oublié' : mode === 'forgot-otp' ? 'Code de vérification' : 'Nouveau mot de passe'}
              </h2>
              <p>
                {mode === 'login'
                  ? 'Accédez à votre espace locataire'
                  : mode === 'forgot-email'
                    ? 'Entrez votre email pour recevoir un code.'
                    : mode === 'forgot-otp'
                      ? 'Entrez le code envoyé par email.'
                      : 'Choisissez un nouveau mot de passe.'}
              </p>
            </div>

            {info && <div className="login-info">{info}</div>}
            {error && <div className="error-message">{error}</div>}

            {mode === 'login' ? (
              <form onSubmit={handleSubmit} className="login-form adeef-uiform">
                <div className="form-group">
                  <label className="adeef-uiform-field" htmlFor="email">
                    <input
                      className="adeef-uiform-input"
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder=" "
                      autoComplete="email"
                      pattern={EMAIL_INPUT_PATTERN}
                      title={EMAIL_INPUT_TITLE}
                    />
                    <span>Adresse email</span>
                  </label>
                </div>

                <div className="form-group">
                  <label className="adeef-uiform-field" htmlFor="password">
                    <input
                      className="adeef-uiform-input"
                      type="password"
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      placeholder=" "
                      autoComplete="current-password"
                    />
                    <span>Mot de passe</span>
                  </label>

                  <button type="button" className="forgot-link" onClick={startForgotPassword}>
                    Mot de passe oublié ?
                  </button>
                </div>

                <button
                  type="submit"
                  className="login-btn"
                  disabled={loading}
                >
                  {loading ? 'Connexion...' : 'Se connecter'}
                </button>
              </form>
            ) : mode === 'forgot-email' ? (
              <form onSubmit={submitForgotEmail} className="login-form adeef-uiform">
                <div className="form-group">
                  <label className="adeef-uiform-field" htmlFor="resetEmail">
                    <input
                      className="adeef-uiform-input"
                      type="email"
                      id="resetEmail"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      required
                      placeholder=" "
                      autoComplete="email"
                      pattern={EMAIL_INPUT_PATTERN}
                      title={EMAIL_INPUT_TITLE}
                    />
                    <span>Adresse email</span>
                  </label>
                </div>

                <button type="submit" className="login-btn" disabled={loading}>
                  {loading ? 'Envoi...' : 'Envoyer le code'}
                </button>

                <button type="button" className="forgot-back" onClick={() => { setMode('login'); setError(''); setInfo(''); }}>
                  ← Retour
                </button>
              </form>
            ) : mode === 'forgot-otp' ? (
              <form onSubmit={submitOtp} className="login-form adeef-uiform">
                <div className="form-group">
                  <label className="adeef-uiform-field" htmlFor="resetOtp">
                    <input
                      className="adeef-uiform-input"
                      type="text"
                      id="resetOtp"
                      value={resetOtp}
                      onChange={(e) => setResetOtp(e.target.value)}
                      required
                      placeholder=" "
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                    />
                    <span>Code (6 chiffres)</span>
                  </label>
                </div>

                <button type="submit" className="login-btn" disabled={loading}>
                  {loading ? 'Vérification...' : 'Vérifier'}
                </button>

                <div className="forgot-actions">
                  <button type="button" className="forgot-link" onClick={resendOtp} disabled={loading}>
                    Renvoyer le code
                  </button>
                  <button type="button" className="forgot-back" onClick={() => { setMode('forgot-email'); setError(''); setInfo(''); }}>
                    ← Changer l'email
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={submitNewPassword} className="login-form adeef-uiform">
                <div className="form-group">
                  <label className="adeef-uiform-field" htmlFor="newPassword">
                    <input
                      className="adeef-uiform-input"
                      type="password"
                      id="newPassword"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      placeholder=" "
                      autoComplete="new-password"
                    />
                    <span>Nouveau mot de passe</span>
                  </label>
                </div>

                <div className="form-group">
                  <label className="adeef-uiform-field" htmlFor="confirmPassword">
                    <input
                      className="adeef-uiform-input"
                      type="password"
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      placeholder=" "
                      autoComplete="new-password"
                    />
                    <span>Confirmer le mot de passe</span>
                  </label>
                </div>

                <button type="submit" className="login-btn" disabled={loading}>
                  {loading ? 'Mise à jour...' : 'Réinitialiser'}
                </button>

                <button type="button" className="forgot-back" onClick={() => { setMode('login'); setError(''); setInfo(''); }}>
                  ← Retour
                </button>
              </form>
            )}

            <div className="login-divider">
              <span>ou</span>
            </div>

            <div className="signup-prompt">
              <p>Vous n'avez pas encore de compte ?</p>
              <Link to="/signup" className="signup-link">Créer un compte</Link>
            </div>

            <div className="back-link">
              <Link to="/">← Retour à l'accueil</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;